/** @jsx jsx */
// eslint-disable-next-line jira/filename-case
import React, { useMemo, useCallback } from 'react';
import { css, jsx } from '@compiled/react';
import InfoIcon from '@atlaskit/icon/core/status-information';
import Inline from '@atlaskit/primitives/inline';
import Stack from '@atlaskit/primitives/stack';
import Pressable from '@atlaskit/primitives/pressable';
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';
import { type FlagComponentProps, InfoFlag } from '@atlassian/jira-flags';
import type { MessageDeliveryStatuses } from '@jira/platform__choreographer-services/src/types.tsx';
import type { Collaborators } from '../../services/get-collaborators/types.tsx';
import { noop } from '../../common/utils.tsx';
import { FlagCompatibleAvatarGroup } from '../../common/ui/flag-compatible-avatar-group/index.tsx';

export type Props = {
	id: FlagComponentProps['id'];
	onDismiss: FlagComponentProps['onDismissed'];
	users: Collaborators;
	onOptoutAndDismiss: () => unknown;
	onSubmit: () => Promise<void>;
	onSnoozeAndDismiss: () => unknown;
	messages: CJNMessageStrings;
	avatarOrientation: 'horizontal' | 'vertical';
	featureVisitedCount: number;
	/**
	 * Fired when the choreographer confirms that the flag was actually displayed
	 * (i.e. message disposition is 'started'). Use this to gate any
	 * "post-display" side effects so they do not run when the flag is blocked.
	 */
	onDisplayed?: () => void;
};

export type CJNMessageStrings = {
	title: string;
	description: string;
	joinButton: string;
	remindMeLaterButton: string;
	notInterestedButton: string;
};

type InfoFlagProps = React.ComponentProps<typeof InfoFlag>;

export const RequestAccessPromptFlag = ({
	users,
	onSubmit,
	onDismiss,
	onOptoutAndDismiss,
	onSnoozeAndDismiss,
	messages,
	avatarOrientation,
	featureVisitedCount,
	onDisplayed,
	...props
}: Props): JSX.Element => {
	const handleOnSubmit = useCallback(async () => {
		await onSubmit();
	}, [onSubmit]);

	const handleMessageDisposition = useCallback(
		(status: MessageDeliveryStatuses) => {
			if (status === 'started') {
				onDisplayed?.();
			}
		},
		[onDisplayed],
	);

	const customFlagFooterProps = useMemo(
		(): Pick<InfoFlagProps, 'actions' | 'linkComponent'> => ({
			// Workaround to achieve primary button + avatars in the footer.
			//
			// How this works:
			// We pass a dummy `action` that gets displayed in the Flag's footer
			// by the custom `linkComponent`. The component displays our actual footer.
			// This way, we don't occupy the limited space inside `description`.
			//
			// Why not use `actions` normally?
			// Because our Avatar Group is not an action, and even our Submit button
			// should be bold & blue (appearance="primary") which is not supported
			// by the AK Flag.
			//
			// Why not put this in `description`?
			// Because that one has a max height equivalent to 5 lines of text,
			// and our footer is taller than 2, causing an unwanted scrollbar
			// to show.
			actions: [
				{
					content: '',
					onClick: noop,
				},
			],
			linkComponent: () => {
				let secondETA = null;

				if (featureVisitedCount === 1) {
					secondETA = (
						<Pressable xcss={linkButtonStyles} onClick={onSnoozeAndDismiss}>
							{messages.remindMeLaterButton}
						</Pressable>
					);
				} else if (featureVisitedCount > 1) {
					secondETA = (
						<Pressable xcss={linkButtonStyles} onClick={onOptoutAndDismiss}>
							{messages.notInterestedButton}
						</Pressable>
					);
				}

				return (
					<Inline alignInline="start" grow="fill" alignBlock="center">
						<Pressable xcss={linkButtonStyles} onClick={handleOnSubmit}>
							{messages.joinButton}
						</Pressable>
						{!!secondETA && <span css={linkButtonSeparatorStyles} />}
						{secondETA}
					</Inline>
				);
			},
		}),
		[featureVisitedCount, handleOnSubmit, messages, onSnoozeAndDismiss, onOptoutAndDismiss],
	);

	const customIcon = useMemo(() => {
		return users.length > 0 ? (
			<FlagCompatibleAvatarGroup
				alignment={avatarOrientation}
				maxAvatarCount={2}
				tooltipPosition="bottom"
				data={users.map((user) => ({
					name: user.name,
					src: user.avatar,
					appearance: 'circle',
				}))}
				size="small"
				boundariesElement="scrollParent"
			/>
		) : (
			<InfoIcon spacing="spacious" color={token('color.icon.information')} label="" />
		);
	}, [users, avatarOrientation]);

	const FLAG_MESSAGE_TYPE = 'marketing';

	return (
		<InfoFlag
			{...props}
			{...customFlagFooterProps}
			onDismissed={onDismiss}
			title={messages.title}
			description={
				<Stack>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p css={mainContentContainerStyles}>{messages.description}</p>
				</Stack>
			}
			icon={customIcon}
			messageType={FLAG_MESSAGE_TYPE}
			messageId="cross-join-nudges.ui.request-access-prompt-flag.info-flag.request-access-prompt-flag"
			{...(onDisplayed && { onMessageDisposition: handleMessageDisposition })}
		/>
	);
};

const linkButtonStyles = xcss({
	maxWidth: '150',
	maxHeight: '3lh',
	overflow: 'hidden',
	color: 'color.link',
	backgroundColor: 'color.background.neutral.subtle',
	borderWidth: '0',
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
	cursor: 'pointer',
	padding: '0',
	':hover': {
		textDecoration: 'underline',
	},
});

const linkButtonSeparatorStyles = css({
	'&::before': {
		content: '·',
		marginLeft: token('space.150'),
		marginRight: token('space.150'),
	},
});

const mainContentContainerStyles = css({
	display: '-webkit-box',
	'-webkit-box-orient': 'vertical',
	overflow: 'visible',
	margin: '0',
	marginBottom: token('space.100'),
});
